import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { getAccount } from "../../../graphql/account/account-queries";

import {
  Box,
  Grid,
  InputAdornment,
  TextField as TextFieldMui,
  Typography,
} from "@material-ui/core";

import {
  Create,
  FileInput,
  FormDataConsumer,
  FormTab,
  Loading,
  RadioButtonGroupInput,
  required,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { useUser } from "../../../authenticatorApp/UserContext";
import { AttachFileIcon, CreatedAtIcon } from "../../../components/common/iconography";
import { AddressInput } from "../../../components/common/AddressInput";
import { BackButton } from "../../../components/common/BackButton";
import { CustomFields } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateTimeInput } from "../../../components/common/YardDateTimeInput";
import { HelpButton } from "../../../components/common/HelpButton";
import { FileField } from "../../../components/common/FileField";
import { logError } from "../../../utils/appUtils";
import { handleFileChange } from "../../../utils/fileUtils";
import { resolveScheduledDateTime } from "../../../utils/dateUtils";
import {
  ACCEPTED_FILES,
  AppType,
  CarrierType,
  CarrierTypes,
  MAX_FILES,
  TrueOrFalse,
  Workflow,
} from "../../../utils/constants";
import { getAddressBook } from "../../../managerApp/resources/outboundShipments/OutboundUtils";
import { resolveDefaultEventType } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import { Scheduler } from "../../../components/common/scheduler/Scheduler";
import { OutboundCreateHelp } from "./OutboundHelp";
import { ValidateCreateButton } from "./ValidateCreateButton";
import { fetchSavedAddresses } from "../../../managerApp/resources/accounts/AccountUtils";

const validateRequired = required();

const fetchData = async (
  appUser,
  setAccount,
  setEventType,
  setCanSchedule,
  setLoading,
  notify
) => {
  try {
    setLoading(true);
    const resp = await API.graphql(
      graphqlOperation(getAccount, { id: appUser.accountId })
    );
    const accountData = resp.data.getAccount;
    const allAddresses = await fetchSavedAddresses(appUser.accountId);
    accountData.savedAddresses = { items: allAddresses };

    // Render account data first so a scheduling-resolution failure below only
    // disables the Scheduler instead of blanking the whole form.
    setAccount(accountData);

    const { eventType, canSchedule } = await resolveDefaultEventType(
      accountData?.outboundEventTypeId,
      Workflow.OUTBOUND
    );
    setEventType(eventType);
    setCanSchedule(canSchedule);
  } catch (err) {
    logError("outbounds:OutboundAdviceCreate:fetchData", err, notify);
  } finally {
    setLoading(false);
  }
};

const ShipmentSetupTab = ({
  appUser,
  account,
  eventType,
  canSchedule,
  selectedTimeslot,
  setSelectedTimeslot,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const [selectedAddress, setSelectedAddress] = useState(null);
  const CarrierTypesReversed = [].concat(CarrierTypes).reverse();
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);

  const getSpacing = () => {
    const spacing = 12 - (account?.outboundAdviceInputs?.length || 1) * 2;
    return Math.max(spacing, 0);
  };

  const handleSchedulerOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSchedule) setIsSchedulerOpen(true);
  };

  const ignoreEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTimeslot = (timeslot) => {
    const scheduledDate = resolveScheduledDateTime(timeslot, {
      notify,
      scope: "outbounds:OutboundAdviceCreate:handleTimeslot",
    });
    if (!scheduledDate) return;
    form.change("scheduledDateTime", scheduledDate);
    setSelectedTimeslot(timeslot);
  };

  const getSchedulerHelperText = () => {
    if (!canSchedule)
      return `Contact ${appUser.reloadYard.name} to setup an event type.`;
    return false;
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="row" spacing={3}>
          <Grid item container direction="column" spacing={0} lg={8}>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} md={4}>
                <TextFieldMui
                  label="Transload Yard"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={appUser?.reloadYard?.name}
                  disabled
                  helperText={false}
                />
              </Grid>
              {/* <Grid item xs={12} md={4} >
              <SelectInput source="site" label="Site" variant="outlined" fullWidth
                choices={[
                  {id:"1", name: "Crescent 1"},
                  {id:"2", name: "Crescent 2"},
                  {id:"2", name: "Dallas 2"},
                ]}
                helperText={false}
              />
            </Grid> */}
              {account?.outboundAdviceInputs?.includes("scheduledDateTime") && (
                <Grid item xs={12} md={4}>
                  <YardDateTimeInput
                    source="scheduledDateTime"
                    label="Schedule Pickup"
                    variant="outlined"
                    fullWidth
                    disabled={!canSchedule}
                    onClick={handleSchedulerOpen}
                    onKeyPress={ignoreEvent}
                    validate={validateRequired}
                    options={{
                      readOnly: true,
                      InputProps: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <CreatedAtIcon onClick={handleSchedulerOpen} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    helperText={getSchedulerHelperText()}
                  />
                  {canSchedule && (
                    <Scheduler
                      eventType={eventType}
                      workflow={Workflow.OUTBOUND}
                      account={account}
                      appType={AppType.CUSTOMER}
                      open={isSchedulerOpen}
                      setIsOpen={setIsSchedulerOpen}
                      resettable={false}
                      selectedTimeslot={selectedTimeslot}
                      setSelectedTimeslot={handleTimeslot}
                    />
                  )}
                </Grid>
              )}
            </Grid>
            <Grid container direction="row" spacing={1}>
              {account?.outboundAdviceInputs?.includes("carrierType") && (
                <Grid item xs={12} sm={6} md={4}>
                  <SelectInput
                    source="carrierType"
                    label="Carrier Type"
                    variant="outlined"
                    fullWidth
                    choices={CarrierTypesReversed}
                    validate={validateRequired}
                    helperText={false}
                  />
                </Grid>
              )}
              {account?.outboundAdviceInputs?.includes("carrier") && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextInput
                    source="carrierName"
                    label="Carrier Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                    helperText={false}
                  />
                </Grid>
              )}
              {account?.outboundAdviceInputs?.includes("railCarNumber") && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextInput
                    source="railCarNumber"
                    label={
                      formData.carrierType === CarrierType.RAIL
                        ? "Railcar Number"
                        : "Trailer Number"
                    }
                    variant="outlined"
                    fullWidth
                    parse={(v) => v.toUpperCase()}
                    validate={validateRequired}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    helperText={false}
                  />
                </Grid>
              )}
              {formData.carrierType === CarrierType.TRUCK &&
                account?.outboundAdviceInputs?.includes("mustTarp") && (
                  <Grid item xs={12} md={4}>
                    <RadioButtonGroupInput
                      source="mustTarp"
                      label="Must Tarp?"
                      row
                      fullWidth
                      choices={TrueOrFalse}
                      defaultValue={account?.mustTarp || false}
                      validate={
                        formData?.carrierType === CarrierType.TRUCK
                          ? validateRequired
                          : undefined
                      }
                      style={{ marginTop: 0, marginLeft: 8 }}
                      helperText={false}
                    />
                  </Grid>
                )}
            </Grid>
            <Grid container direction="row" spacing={1}>
              {account?.outboundAdviceInputs?.includes("poNumber") && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextInput
                    source="poNumber"
                    label="PO Number"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                    validate={validateRequired}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                  />
                </Grid>
              )}
              {formData.carrierType === CarrierType.TRUCK &&
                account?.outboundAdviceInputs?.includes("pickupNumber") && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextInput
                      source="pickupNumber"
                      label="Pickup Number"
                      variant="outlined"
                      fullWidth
                      parse={(v) => v.toUpperCase()}
                      validate={validateRequired}
                    />
                  </Grid>
                )}
              {account?.outboundAdviceInputs?.includes("shipTo") && (
                <Grid item xs={12}>
                  <Typography
                    gutterBottom
                    variant="overline"
                    color="textSecondary"
                  >
                    Consignee Address
                  </Typography>
                  {getAddressBook(
                    account.savedAddresses?.items, // from fetchSavedAddresses
                    null, // billToAccount = account
                    form,
                    selectedAddress,
                    setSelectedAddress
                  )}
                  {!selectedAddress && (
                    <>
                      <AddressInput
                        source="shipTo"
                        variant="outlined"
                        fullWidth
                        showBusinessName={true}
                        required={true}
                        parse={(v) => (v !== "" ? v : undefined)}
                        allValues={formData}
                      />
                    </>
                  )}
                </Grid>
              )}
            </Grid>
            {account?.outboundAdviceInputs?.includes(
              "outboundCustomFields"
            ) && (
              <Box mt={2}>
                <CustomFields
                  {...props}
                  // showHeader={false}
                  accountFields={account?.outboundCustomFields}
                  width={4}
                  disabled={false}
                />
              </Box>
            )}
          </Grid>
          <Grid item container direction="row" spacing={0} xs={false} md={4}>
            {account?.outboundAdviceInputs?.includes("attachments") && (
              <Grid item xs={12}>
                <FileInput
                  source="attachments"
                  label="ATTACHMENTS"
                  variant="outlined"
                  fullWidth
                  placeholder={
                    <p>
                      <AttachFileIcon fontSize="large" /> <br /> Drop files to
                      upload, or click to select.
                    </p>
                  }
                  accept={ACCEPTED_FILES}
                  multiple
                  maxFiles={MAX_FILES}
                  options={{ maxFiles: MAX_FILES }}
                  onChange={(value) =>
                    handleFileChange("attachments", value, form, notify)
                  }
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </Grid>
            )}
            {account?.outboundAdviceInputs?.includes("bolFile") && (
              <Grid item xs={12}>
                <FileInput
                  source="bolFile"
                  label="BOL FILE"
                  variant="outlined"
                  fullWidth
                  placeholder={
                    <p>
                      <AttachFileIcon fontSize="large" /> <br /> Drop BOL file
                      to upload, or click to select.
                    </p>
                  }
                  accept={ACCEPTED_FILES}
                  onChange={(value) =>
                    handleFileChange("bolFile", value, form, notify)
                  }
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box mt={getSpacing()}></Box>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const OutboundCreateActions = ({ basePath, ...props }) => (
  <TopToolbar>
    <HelpButton>
      <OutboundCreateHelp {...props} />
    </HelpButton>
    <BackButton basePath={basePath} />
  </TopToolbar>
);

const OutboundCreateToolbar = ({
  appUser,
  account,
  selectedTimeslot,
  ...props
}) => {
  return (
    <Toolbar {...props}>
      <ValidateCreateButton
        label="Save"
        appUser={appUser}
        account={account}
        selectedTimeslot={selectedTimeslot}
      />
    </Toolbar>
  );
};

export const OutboundAdviceCreate = (props) => {
  const notify = useNotify();
  const { appUser } = useUser();
  const [account, setAccount] = useState();
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState();
  const [canSchedule, setCanSchedule] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState();
  // const [shipmentLineItems, setShipmentLineItems] = useState({ items: [] });

  useEffect(() => {
    fetchData(appUser, setAccount, setEventType, setCanSchedule, setLoading, notify);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser]);

  if (loading) return <Loading />;
  return (
    <Create
      {...props}
      title={<ForceTitle title="New Outbound" />}
      actions={<OutboundCreateActions {...props} />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <OutboundCreateToolbar
            appUser={appUser}
            account={account}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormTab label="Outbound Setup">
          <ShipmentSetupTab
            {...props}
            appUser={appUser}
            account={account}
            eventType={eventType}
            canSchedule={canSchedule}
            selectedTimeslot={selectedTimeslot}
            setSelectedTimeslot={setSelectedTimeslot}
          />
        </FormTab>
        {/* {
          TODO: consider pickList design for customers. if FIFO show products, elee if CARLOT shoe Inventories
          account?.outboundAdviceInputs?.includes("shipmentLineItems") &&
          <FormTab label="Pick List">
            <PickListTab {...props}
              account={account}
              shipmentLineItems={shipmentLineItems} setShipmentLineItems={setShipmentLineItems}
            />
          </FormTab>
        } */}
      </TabbedForm>
    </Create>
  );
};
